/**
 * Semantic similarity as three presets (broad, balanced, strict) with a custom escape hatch that reveals the
 * numeric slider. Cosine scores sit in a narrow 0.59 to 0.70 band, so a raw 0..1 slider reads as noise; the
 * presets name the useful points and the API keeps receiving the float.
 */
import { useId, useState } from 'react';
import SegmentedControl from '@/components/arc/segmented-control/segmented-control';
import { Slider } from '@/components/arc/slider/slider';
import styles from './SimilarityControl.module.css';

export interface SimilarityPreset {
  value: 'broad' | 'balanced' | 'strict';
  label: string;
  threshold: number;
  hint: string;
}

export const SIMILARITY_PRESETS: SimilarityPreset[] = [
  { value: 'broad', label: 'Broad', threshold: 0.55, hint: 'More moments, looser matches.' },
  { value: 'balanced', label: 'Balanced', threshold: 0.62, hint: 'A good starting point for most briefs.' },
  { value: 'strict', label: 'Strict', threshold: 0.7, hint: 'Fewer moments, only the closest matches.' },
];
export const DEFAULT_SIMILARITY = 0.62;
export const SIMILARITY_MIN = 0.3;
export const SIMILARITY_MAX = 0.95;

/** The preset a stored threshold corresponds to, or null for a custom value. */
export function similarityPreset(threshold: number): SimilarityPreset | null {
  return SIMILARITY_PRESETS.find((p) => Math.abs(p.threshold - threshold) < 0.0005) ?? null;
}

export interface SimilarityControlProps {
  label: string;
  value: number;
  onValueChange: (value: number) => void;
  /** Runs when a preset is picked or the custom slider is released. */
  onValueCommit?: (value: number) => void;
  disabled?: boolean;
}

export function SimilarityControl({ label, value, onValueChange, onValueCommit, disabled }: SimilarityControlProps) {
  const id = useId();
  const preset = similarityPreset(value);
  // Once the person opens the slider it stays open, even when they drag across a preset's value.
  const [custom, setCustom] = useState(() => preset === null);
  const selected = custom || preset === null ? 'custom' : preset.value;
  const hint =
    selected === 'custom' ? 'Cosine similarity. Scores above 0.70 are rare.' : (preset?.hint ?? '');

  const choose = (next: string) => {
    if (next === 'custom') {
      setCustom(true);
      return;
    }
    const target = SIMILARITY_PRESETS.find((p) => p.value === next);
    if (!target) return;
    setCustom(false);
    onValueChange(target.threshold);
    onValueCommit?.(target.threshold);
  };

  return (
    <div className={styles.root} role="group" aria-labelledby={`${id}-label`} aria-disabled={disabled || undefined}>
      <div className={styles.head}>
        <span id={`${id}-label`} className={styles.label}>
          {label}
        </span>
        <span className={styles.readout} aria-live="polite">
          {value.toFixed(2)}
        </span>
      </div>
      <SegmentedControl
        label={label}
        value={selected}
        onValueChange={choose}
        options={[
          ...SIMILARITY_PRESETS.map((p) => ({ value: p.value, label: p.label })),
          { value: 'custom', label: 'Custom' },
        ]}
      />
      {selected === 'custom' ? (
        <Slider
          label="Custom similarity"
          value={value}
          onValueChange={onValueChange}
          onValueCommit={onValueCommit}
          min={SIMILARITY_MIN}
          max={SIMILARITY_MAX}
          step={0.01}
          marks={SIMILARITY_PRESETS.map((p) => p.threshold)}
          format={(v) => v.toFixed(2)}
          showValue={false}
          disabled={disabled}
        />
      ) : null}
      <p className={styles.hint}>{hint}</p>
    </div>
  );
}
